import {
  AppBar,
  Button,
  Collapse,
  Container,
  Drawer,
  Grid,
  Hidden,
  IconButton,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import MenuIcon from '@material-ui/icons/Menu';
import React from 'react';
import Link from '../link';

export type SimpleHeaderLink = {
  label: string;
  path: string;
};

export type HeaderLink =
  | SimpleHeaderLink
  | {
      label: string;
      children: SimpleHeaderLink[];
    };

export type HeaderProps = {
  links?: HeaderLink[];
  mobileLinks?: HeaderLink[];
  className?: string;
};

const useStyles = makeStyles(theme => ({
  appbar: {
    boxShadow: '0px 4px 12px rgba(18, 30, 52, 0.06)',
    background: '#fff',
  },
  logo: {
    display: 'block',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(4),
    },
  },
  drawerBtnContainer: {
    height: '100%',
    marginRight: theme.spacing(2),
  },
  menuBtnBase: {
    textTransform: 'none',
    fontWeight: 400,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),

    [theme.breakpoints.down('md')]: {
      fontSize: '0.9rem',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  menuBtnLeft: {
    color: theme.palette.grey[800],
    height: '100%',
    borderRadius: 0,
    marginRight: theme.spacing(2),

    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(1),
    },
  },
  subMenuItem: {
    fontSize: '0.95rem',
    fontWeight: 400,

    '& a': {
      color: theme.palette.text.primary,
      textDecoration: 'none',
    },
  },
  mobileNavList: {
    width: '90%',
  },
  nestedListItem: {
    paddingLeft: theme.spacing(4),
  },
  drawerLink: {
    color: theme.palette.text.primary,
    textDecoration: 'none',
  },
}));

const Header: React.FC<HeaderProps> = props => {
  const classes = useStyles();

  const [isDrawerOpen, setDrawerState] = React.useState(false);
  const toggleDrawer = () => setDrawerState(!isDrawerOpen);

  let appbarClassName = classes.appbar;
  if (props.className) {
    appbarClassName = `${props.className} ${appbarClassName}`;
  }

  const desktopLinks = props.links || [];
  const mobileLinks = props.mobileLinks || props.links;

  return (
    <>
      <AppBar position="static" className={appbarClassName}>
        <Container maxWidth="xl">
          <Grid container justifyContent="space-between" alignItems="stretch">
            <Grid item>
              <Grid direction="row" container alignItems="stretch">
                <Hidden mdUp>
                  <Grid item>
                    <Grid container alignItems="center" className={classes.drawerBtnContainer}>
                      <Grid item>
                        <IconButton edge="start" onClick={toggleDrawer}>
                          <MenuIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Hidden>
                <Grid item>
                  <Link to="/" className={classes.logo}>
                    <img src="/img/logo.svg" alt="architect" />
                  </Link>
                </Grid>
                <Hidden smDown>
                  {desktopLinks.map((link, index) => {
                    if ('children' in link) {
                      const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

                      const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                        setAnchorEl(event.currentTarget);
                      };

                      const handleClose = () => {
                        setAnchorEl(null);
                      };

                      return (
                        <Grid item key={index}>
                          <Button
                            onClick={handleClick}
                            className={`${classes.menuBtnBase} ${classes.menuBtnLeft}`}
                            endIcon={<ArrowDropDownIcon />}
                          >
                            {link.label}
                          </Button>
                          {'children' in link && (
                            <Menu
                              anchorEl={anchorEl}
                              keepMounted
                              open={Boolean(anchorEl)}
                              onClose={handleClose}
                              getContentAnchorEl={null}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            >
                              {link.children.map(child => (
                                <Link to={child.path} key={child.path}>
                                  <MenuItem onClick={handleClose} className={classes.subMenuItem}>
                                    {child.label}
                                  </MenuItem>
                                </Link>
                              ))}
                            </Menu>
                          )}
                        </Grid>
                      );
                    } else {
                      return (
                        <Grid item key={index}>
                          <Link to={link.path} className={classes.drawerLink}>
                            <Button className={`${classes.menuBtnBase} ${classes.menuBtnLeft}`}>
                              {link.label}
                            </Button>
                          </Link>
                        </Grid>
                      );
                    }
                  })}
                </Hidden>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container direction="row" alignItems="center" style={{ height: '100%' }}>
                <Hidden xsDown>
                  <Grid item>
                    <Button
                      className={classes.menuBtnBase}
                      style={{ marginRight: 16 }}
                      href="//cloud.architect.io/login"
                    >
                      Login
                    </Button>
                  </Grid>
                </Hidden>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.menuBtnBase}
                    href="//cloud.architect.io/signup"
                  >
                    Get started
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </AppBar>
      <Drawer
        classes={{ paper: classes.mobileNavList }}
        anchor="left"
        open={isDrawerOpen}
        onClose={toggleDrawer}
      >
        <List>
          <ListItem>
            <Link to="/">
              <img src="/img/logo.svg" alt="architect" />
            </Link>
          </ListItem>
          {mobileLinks.map((link, index) => {
            if ('children' in link) {
              const [open, setOpen] = React.useState(false);

              const handleClick = () => {
                setOpen(!open);
              };

              return (
                <div key={index}>
                  <ListItem button onClick={handleClick}>
                    <ListItemText>{link.label}</ListItemText>
                    {open ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {link.children.map(child => (
                        <Link key={child.path} to={child.path} className={classes.drawerLink}>
                          <ListItem button className={classes.nestedListItem}>
                            <ListItemText>{child.label}</ListItemText>
                          </ListItem>
                        </Link>
                      ))}
                    </List>
                  </Collapse>
                </div>
              );
            } else {
              return (
                <Link key={index} to={link.path} className={classes.drawerLink}>
                  <ListItem button key={index}>
                    <ListItemText>{link.label}</ListItemText>
                  </ListItem>
                </Link>
              );
            }
          })}
        </List>
      </Drawer>
    </>
  );
};

Header.defaultProps = {
  links: [],
};

export default Header;
